import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { ArrowLeft, Download, CalendarIcon, Clock, User, ClipboardList, Edit, Trash2, AlertCircle, Check, X } from "lucide-react";
import { format, addDays } from "date-fns";
import html2canvas from "html2canvas";
import logoImage from "@/assets/skylink-logo.png";
import Footer from "@/components/Footer";

interface SlotData {
  id: string;
  time_start: string;
  time_end: string;
  slot_date: string | null;
  instructor_id: string;
  subject_id: string;
  batch_id: string | null;
  notes: string;
  created_by: string | null;
  status: string;
  instructor_name?: string;
  subject_name?: string;
  batch_name?: string;
  students?: string[];
}

export default function ViewSlots() {
  const navigate = useNavigate();
  const downloadRef = useRef<HTMLDivElement>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [showMultiDateDialog, setShowMultiDateDialog] = useState(false);
  const [slots, setSlots] = useState<SlotData[]>([]);
  const [multiDateSlots, setMultiDateSlots] = useState<Record<string, SlotData[]>>({});
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [instructorBookedDates, setInstructorBookedDates] = useState<Date[]>([]);
  const [editingSlot, setEditingSlot] = useState<SlotData | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [newTimeStart, setNewTimeStart] = useState("");
  const [newTimeEnd, setNewTimeEnd] = useState("");
  const [deletingSlot, setDeletingSlot] = useState<SlotData | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const attendanceDownloadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    checkAuth();
    fetchInstructorBookedDates();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchSlots();
    }
  }, [selectedDate]);

  const fetchInstructorBookedDates = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get instructor ID for current user
      const { data: instructor } = await supabase
        .from("instructors")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!instructor) return;

      // Fetch all dates where this instructor has booked slots
      const { data: bookedSlots } = await supabase
        .from("flight_slots")
        .select("slot_date")
        .eq("instructor_id", instructor.id);

      if (bookedSlots) {
        const uniqueDates = [...new Set(bookedSlots.map(slot => slot.slot_date))];
        setInstructorBookedDates(uniqueDates.map(dateStr => new Date(dateStr + 'T00:00:00')));
      }
    } catch (error) {
      console.error("Error fetching instructor booked dates:", error);
    }
  };

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
      return;
    }

    setCurrentUserId(user.id);

    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id);

    setIsAdmin(roles?.some(r => r.role === "admin" || r.role === "manager" || r.role === "super_admin") || false);
  };

  const fetchSlots = async () => {
    setLoading(true);
    const dateStr = format(selectedDate, "yyyy-MM-dd");

    // Fetch slots for the selected date
    const { data: slotsData, error: slotsError } = await supabase
      .from("flight_slots")
      .select("*")
      .eq("slot_date", dateStr)
      .order("time_start");

    if (slotsError) {
      toast.error("Failed to fetch slots");
      setLoading(false);
      return;
    }

    if (!slotsData || slotsData.length === 0) {
      setSlots([]);
      setLoading(false);
      return;
    }

    // Fetch related data
    const instructorIds = [...new Set(slotsData.map(s => s.instructor_id))];
    const subjectIds = [...new Set(slotsData.map(s => s.subject_id))];
    const batchIds = [...new Set(slotsData.filter(s => s.batch_id).map(s => s.batch_id!))];

    const [instructorsRes, subjectsRes, batchesRes] = await Promise.all([
      supabase.from("instructors").select("id, name").in("id", instructorIds),
      supabase.from("subjects").select("id, name").in("id", subjectIds),
      batchIds.length > 0 
        ? supabase.from("batches").select("id, name").in("id", batchIds)
        : Promise.resolve({ data: [] }),
    ]);

    // Fetch students for each slot
    const slotIds = slotsData.map(s => s.id);
    const { data: slotStudents } = await supabase
      .from("flight_slot_students")
      .select("slot_id, student_id, students(first_name, last_name)")
      .in("slot_id", slotIds);

    const enrichedSlots: SlotData[] = slotsData.map(slot => {
      const instructor = instructorsRes.data?.find(i => i.id === slot.instructor_id);
      const subject = subjectsRes.data?.find(s => s.id === slot.subject_id);
      const batch = batchesRes.data?.find(b => b.id === slot.batch_id);
      
      const students = slotStudents
        ?.filter(ss => ss.slot_id === slot.id)
        .map(ss => {
          const student = ss.students as any;
          return `${student?.first_name || ""} ${student?.last_name || ""}`.trim();
        }) || [];

      return {
        ...slot,
        instructor_name: instructor?.name || "Unknown",
        subject_name: subject?.name || "Unknown",
        batch_name: batch?.name || "No Batch",
        students,
      };
    });

    setSlots(enrichedSlots);
    setLoading(false);
  };

  const handleEditClick = (slot: SlotData) => {
    setEditingSlot(slot);
    setNewTimeStart(slot.time_start);
    setNewTimeEnd(slot.time_end);
    setShowEditDialog(true);
    setPinInput("");
  };

  const logAction = async (action: string, details: any, pinUsed: boolean = false) => {
    try {
      await supabase.from("admin_logs").insert({
        user_id: currentUserId,
        action,
        details,
        pin_used: pinUsed,
      });
    } catch (error) {
      console.error("Failed to log action:", error);
    }
  };

  const handleEditConfirm = async () => {
    if (!editingSlot || !newTimeStart || !newTimeEnd) {
      toast.error("Please fill in all time fields");
      return;
    }

    if (newTimeStart >= newTimeEnd) {
      toast.error("End time must be after start time");
      return;
    }

    try {
      // Verify PIN using server-side function
      const { data: isVerified, error: verifyError } = await supabase.rpc('verify_admin_action', {
        _pin: pinInput,
        _action: 'Edit Slot Time',
        _details: {
          slot_id: editingSlot.id,
          instructor: editingSlot.instructor_name,
          old_time: `${editingSlot.time_start} - ${editingSlot.time_end}`,
          new_time: `${newTimeStart} - ${newTimeEnd}`,
          date: editingSlot.slot_date,
        }
      });

      if (verifyError || !isVerified) {
        toast.error("Incorrect PIN or unauthorized");
        setPinInput("");
        return;
      }

      const { error } = await supabase
        .from("flight_slots")
        .update({
          time_start: newTimeStart,
          time_end: newTimeEnd,
        })
        .eq("id", editingSlot.id);

      if (error) throw error;

      toast.success("Slot time updated successfully");
      setShowEditDialog(false);
      setEditingSlot(null);
      setPinInput("");
      fetchSlots();
    } catch (error: any) {
      toast.error("Failed to update slot");
    }
  };

  const handleDeleteClick = (slot: SlotData) => {
    setDeletingSlot(slot);
    setShowDeleteDialog(true);
    setPinInput("");
  };

  const handleDeleteConfirm = async () => {
    if (!deletingSlot) return;

    try {
      // Verify PIN using server-side function (only for admins)
      if (isAdmin) {
        const { data: isVerified, error: verifyError } = await supabase.rpc('verify_admin_action', {
          _pin: pinInput,
          _action: 'Delete Slot',
          _details: {
            slot_id: deletingSlot.id,
            instructor: deletingSlot.instructor_name,
            time: `${deletingSlot.time_start} - ${deletingSlot.time_end}`,
            date: deletingSlot.slot_date,
          }
        });

        if (verifyError || !isVerified) {
          toast.error("Incorrect PIN or unauthorized");
          setPinInput("");
          return;
        }
      }

      const { error } = await supabase
        .from("flight_slots")
        .delete()
        .eq("id", deletingSlot.id);

      if (error) throw error;

      if (isAdmin) {
        await logAction("Delete Slot", {
          slot_id: deletingSlot.id,
          instructor: deletingSlot.instructor_name,
          subject: deletingSlot.subject_name,
          time: `${deletingSlot.time_start} - ${deletingSlot.time_end}`,
          date: deletingSlot.slot_date,
        }, true);
      }

      toast.success("Slot deleted successfully");
      setShowDeleteDialog(false);
      setDeletingSlot(null);
      setPinInput("");
      fetchSlots();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete slot");
    }
  };

  const canDeleteSlot = (slot: SlotData) => {
    // Admins can delete any slot
    if (isAdmin) return true;
    // Instructors can only delete their own slots
    return slot.created_by === currentUserId;
  };

  const handleApproveSlot = async (slot: SlotData) => {
    try {
      const { error } = await supabase
        .from("flight_slots")
        .update({ status: "approved" })
        .eq("id", slot.id);

      if (error) throw error;

      await logAction("Approve Slot Request", {
        slot_id: slot.id,
        instructor: slot.instructor_name,
        subject: slot.subject_name,
        time: `${slot.time_start} - ${slot.time_end}`,
        date: slot.slot_date,
      }, false);

      toast.success("Slot request approved");
      fetchSlots();
    } catch (error: any) {
      toast.error("Failed to approve slot");
    }
  };

  const handleRejectSlot = async (slot: SlotData) => {
    try {
      const { error } = await supabase
        .from("flight_slots")
        .update({ status: "rejected" })
        .eq("id", slot.id);

      if (error) throw error;

      await logAction("Reject Slot Request", {
        slot_id: slot.id,
        instructor: slot.instructor_name,
        subject: slot.subject_name,
        time: `${slot.time_start} - ${slot.time_end}`,
        date: slot.slot_date,
      }, false);

      toast.success("Slot request rejected");
      fetchSlots();
    } catch (error: any) {
      toast.error("Failed to reject slot");
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-500/10 text-green-600 border border-green-500/20">Approved</span>;
      case "pending":
        return <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-yellow-500/10 text-yellow-600 border border-yellow-500/20">Pending Approval</span>;
      case "rejected":
        return <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-red-500/10 text-red-600 border border-red-500/20">Rejected</span>;
      default:
        return null;
    }
  };

  const handleDownload = async () => {
    if (!downloadRef.current) return;

    try {
      const canvas = await html2canvas(downloadRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        width: 800,
        windowWidth: 800,
      });

      const link = document.createElement("a");
      link.download = `slot-schedule-${format(selectedDate, "yyyy-MM-dd")}.png`;
      link.href = canvas.toDataURL();
      link.click();

      toast.success("Schedule downloaded!");
    } catch (error) {
      toast.error("Failed to download schedule");
    }
  };

  const handleMultiDateSelect = () => {
    setShowMultiDateDialog(true);
  };

  const toggleDateSelection = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const exists = selectedDates.find(d => format(d, "yyyy-MM-dd") === dateStr);
    
    if (exists) {
      setSelectedDates(selectedDates.filter(d => format(d, "yyyy-MM-dd") !== dateStr));
    } else {
      setSelectedDates([...selectedDates, date]);
    }
  };

  const fetchMultiDateSlots = async () => {
    if (selectedDates.length === 0) {
      toast.error("Please select at least one date");
      return;
    }

    setLoading(true);
    const dateStrings = selectedDates.map(d => format(d, "yyyy-MM-dd"));

    const { data: slotsData, error: slotsError } = await supabase
      .from("flight_slots")
      .select("*")
      .in("slot_date", dateStrings)
      .order("slot_date")
      .order("time_start");

    if (slotsError || !slotsData || slotsData.length === 0) {
      toast.error("No slots found for selected dates");
      setLoading(false);
      return;
    }

    const instructorIds = [...new Set(slotsData.map(s => s.instructor_id))];
    const subjectIds = [...new Set(slotsData.map(s => s.subject_id))];
    const batchIds = [...new Set(slotsData.filter(s => s.batch_id).map(s => s.batch_id!))];

    const [instructorsRes, subjectsRes, batchesRes, slotStudentsRes] = await Promise.all([
      supabase.from("instructors").select("id, name").in("id", instructorIds),
      supabase.from("subjects").select("id, name").in("id", subjectIds),
      batchIds.length > 0 
        ? supabase.from("batches").select("id, name").in("id", batchIds)
        : Promise.resolve({ data: [] }),
      supabase
        .from("flight_slot_students")
        .select("slot_id, student_id, students(first_name, last_name)")
        .in("slot_id", slotsData.map(s => s.id)),
    ]);

    const groupedSlots: Record<string, SlotData[]> = {};
    
    slotsData.forEach(slot => {
      const dateKey = slot.slot_date || "";
      if (!groupedSlots[dateKey]) {
        groupedSlots[dateKey] = [];
      }

      const instructor = instructorsRes.data?.find(i => i.id === slot.instructor_id);
      const subject = subjectsRes.data?.find(s => s.id === slot.subject_id);
      const batch = batchesRes.data?.find(b => b.id === slot.batch_id);
      
      const students = slotStudentsRes.data
        ?.filter(ss => ss.slot_id === slot.id)
        .map(ss => {
          const student = ss.students as any;
          return `${student?.first_name || ""} ${student?.last_name || ""}`.trim();
        }) || [];

      groupedSlots[dateKey].push({
        ...slot,
        instructor_name: instructor?.name || "Unknown",
        subject_name: subject?.name || "Unknown",
        batch_name: batch?.name || "No Batch",
        students,
      });
    });

    setMultiDateSlots(groupedSlots);
    setLoading(false);
    setShowMultiDateDialog(false);
    
    setTimeout(() => {
      handleMultiDateDownload();
    }, 100);
  };

  const handleMultiDateDownload = async () => {
    const multiRef = document.getElementById("multi-date-download");
    if (!multiRef) return;

    try {
      const canvas = await html2canvas(multiRef, {
        backgroundColor: "#ffffff",
        scale: 2,
        width: 800,
        windowWidth: 800,
      });

      const link = document.createElement("a");
      const startDate = format(selectedDates[0], "yyyy-MM-dd");
      const endDate = format(selectedDates[selectedDates.length - 1], "yyyy-MM-dd");
      link.download = `slots-${startDate}-to-${endDate}.png`;
      link.href = canvas.toDataURL();
      link.click();

      toast.success("Multi-date schedule downloaded!");
      setMultiDateSlots({});
      setSelectedDates([]);
    } catch (error) {
      toast.error("Failed to download schedule");
    }
  };

  const downloadAttendanceForSlot = async (slot: SlotData) => {
    if (!attendanceDownloadRef.current) return;

    // Fetch attendance data for this slot
    const { data: attendanceData } = await supabase
      .from("student_attendance")
      .select("status, students(first_name, last_name)")
      .eq("instructor_id", slot.instructor_id)
      .eq("subject_id", slot.subject_id)
      .eq("date", slot.slot_date);

    const presentStudents = attendanceData
      ?.filter(a => a.status === "Present")
      .map(a => {
        const student = a.students as any;
        return `${student?.first_name || ""} ${student?.last_name || ""}`.trim();
      }) || [];

    // Create temporary element for download
    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-9999px";
    tempDiv.style.width = "800px";
    tempDiv.innerHTML = `
      <div style="background: white; padding: 32px;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; border-bottom: 2px solid #dc2626; padding-bottom: 16px;">
          <img src="${logoImage}" alt="Logo" style="height: 64px;" />
          <div style="text-align: right;">
            <h1 style="font-size: 24px; font-weight: bold; color: #dc2626; margin: 0;">Attendance Report</h1>
            <p style="font-size: 14px; color: #666; margin: 4px 0 0 0;">Skylink Aviation</p>
          </div>
        </div>
        <div style="margin-bottom: 24px;">
          <p style="margin: 8px 0;"><strong>Date:</strong> ${slot.slot_date && format(new Date(slot.slot_date), "PPP")}</p>
          <p style="margin: 8px 0;"><strong>Instructor:</strong> ${slot.instructor_name}</p>
          <p style="margin: 8px 0;"><strong>Subject:</strong> ${slot.subject_name}</p>
          <p style="margin: 8px 0;"><strong>Time:</strong> ${slot.time_start} - ${slot.time_end}</p>
        </div>
        <div style="margin-bottom: 16px;">
          <h3 style="font-size: 16px; font-weight: 600; margin-bottom: 12px;">Students Present (${presentStudents.length})</h3>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
            ${presentStudents.map(s => `<div style="background: #f3f4f6; padding: 8px; border-radius: 4px; font-size: 14px;">${s}</div>`).join('')}
          </div>
        </div>
        <div style="margin-top: 24px; padding-top: 16px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 12px; color: #666;">
          Generated on ${format(new Date(), "PPP 'at' p")}
        </div>
      </div>
    `;
    document.body.appendChild(tempDiv);

    try {
      const canvas = await html2canvas(tempDiv, {
        backgroundColor: "#ffffff",
        scale: 2,
        width: 800,
        windowWidth: 800,
      });

      const link = document.createElement("a");
      link.download = `attendance-${slot.slot_date}-${slot.instructor_name}.png`;
      link.href = canvas.toDataURL();
      link.click();

      toast.success("Attendance downloaded!");
    } catch (error) {
      toast.error("Failed to download attendance");
    } finally {
      document.body.removeChild(tempDiv);
    }
  };

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      <div className="flex-1 container mx-auto p-3 sm:p-6 max-w-full sm:max-w-6xl overflow-x-hidden">
        <div className="flex flex-wrap gap-2 sm:gap-4 mb-4 sm:mb-6">
          <Button onClick={() => navigate("/dashboard")} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
        {slots.length > 0 && (
          <>
            <Button onClick={handleDownload} className="text-xs sm:text-sm">
              <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Download Day
            </Button>
            <Button onClick={handleMultiDateSelect} variant="outline" className="text-xs sm:text-sm">
              <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Multi-Date Download
            </Button>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 overflow-x-hidden">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarIcon className="w-5 h-5 text-primary" />
              Select Date
            </CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="pointer-events-auto"
              modifiers={{
                booked: instructorBookedDates
              }}
              modifiersStyles={{
                booked: { backgroundColor: '#fee2e2', color: '#991b1b' }
              }}
            />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <ClipboardList className="w-5 h-5 text-primary" />
              Slots for {format(selectedDate, "PPP")}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            {loading ? (
              <div className="flex items-center justify-center gap-2 p-8">
                <ClipboardList className="w-5 h-5 text-primary animate-pulse" />
                <span className="text-sm">Loading slots...</span>
              </div>
            ) : slots.length === 0 ? (
              <div className="text-center p-8 text-muted-foreground">
                <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No slots booked for this date</p>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                {slots.map((slot) => (
                  <div
                    key={slot.id}
                    className={`p-3 sm:p-4 border rounded-lg bg-card hover:bg-accent/5 transition-colors ${
                      slot.status === "pending" ? "border-yellow-500/50" : 
                      slot.status === "rejected" ? "border-red-500/50 opacity-60" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      {getStatusBadge(slot.status)}
                      <div className="flex flex-wrap gap-2">
                        {/* Approve/Reject buttons for admins on pending slots */}
                        {isAdmin && slot.status === "pending" && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs bg-green-500/10 hover:bg-green-500/20 text-green-600 border-green-500/30"
                              onClick={() => handleApproveSlot(slot)}
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs bg-red-500/10 hover:bg-red-500/20 text-red-600 border-red-500/30"
                              onClick={() => handleRejectSlot(slot)}
                            >
                              <X className="w-3 h-3 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        {canDeleteSlot(slot) && slot.status === "approved" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadAttendanceForSlot(slot)}
                            className="text-xs"
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Attendance
                          </Button>
                        )}
                        {isAdmin && slot.status === "approved" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditClick(slot)}
                            className="text-xs"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                        )}
                        {canDeleteSlot(slot) && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteClick(slot)}
                            className="text-xs"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3">
                      <div className="flex items-start gap-2">
                        <User className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">Instructor</p>
                          <p className="text-sm font-medium truncate">{slot.instructor_name}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <ClipboardList className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">Subject</p>
                          <p className="text-sm font-medium truncate">{slot.subject_name}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Clock className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">Time</p>
                          <p className="text-sm font-medium">
                            {slot.time_start} - {slot.time_end}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <User className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">Batch</p>
                          <p className="text-sm font-medium truncate">{slot.batch_name}</p>
                        </div>
                      </div>
                    </div>
                    {slot.students && slot.students.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-muted-foreground mb-2">Students:</p>
                        <div className="flex flex-wrap gap-1.5 sm:gap-2">
                          {slot.students.map((student, idx) => (
                            <span
                              key={idx}
                              className="text-xs bg-muted px-2 py-1 rounded break-all"
                            >
                              {student}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {slot.notes && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-muted-foreground mb-1">Notes:</p>
                        <p className="text-sm break-words">{slot.notes}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </div>

      {/* Hidden downloadable view */}
      {slots.length > 0 && (
        <div className="fixed top-0 left-0 w-0 h-0 overflow-hidden" aria-hidden="true">
          <div ref={downloadRef} style={{ backgroundColor: 'white', padding: '24px', width: '800px', maxWidth: '800px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px', gap: '12px' }}>
              <img src={logoImage} alt="Skylink Aviation" style={{ height: '40px', width: 'auto', flexShrink: 0 }} />
              <div style={{ textAlign: 'right', flexShrink: 0, maxWidth: '400px' }}>
                <h1 style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc2626', overflowWrap: 'break-word' }}>Slot Schedule</h1>
                <p style={{ fontSize: '12px', color: '#4b5563', overflowWrap: 'break-word' }}>Skylink Aviation</p>
                <p style={{ fontSize: '12px', color: '#1f2937', marginTop: '4px', fontWeight: '600', overflowWrap: 'break-word' }}>
                  {format(selectedDate, "PPPP")}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {slots.map((slot, idx) => (
                <div key={slot.id} style={{ border: '2px solid #dc2626', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ backgroundColor: '#dc2626', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                      #{idx + 1}
                    </span>
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                      {slot.time_start} - {slot.time_end}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '8px' }}>
                    <div>
                      <p style={{ fontSize: '12px', color: '#6b7280' }}>Instructor</p>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{slot.instructor_name}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: '#6b7280' }}>Subject</p>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{slot.subject_name}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: '#6b7280' }}>Batch</p>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{slot.batch_name}</p>
                    </div>
                  </div>
                  {slot.students && slot.students.length > 0 && (
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Students:</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {slot.students.map((student, sidx) => (
                          <span key={sidx} style={{ fontSize: '12px', backgroundColor: '#f3f4f6', padding: '2px 8px', borderRadius: '4px', color: '#111827' }}>
                            {student}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ marginTop: '16px', paddingTop: '8px', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
              <p style={{ fontSize: '12px', color: '#6b7280' }}>Generated on {format(new Date(), "Pp")}</p>
            </div>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Edit Slot Time</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Editing slot for <strong>{editingSlot?.instructor_name}</strong>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="editTimeStart">Start Time</Label>
                <Input
                  id="editTimeStart"
                  type="time"
                  value={newTimeStart}
                  onChange={(e) => setNewTimeStart(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="editTimeEnd">End Time</Label>
                <Input
                  id="editTimeEnd"
                  type="time"
                  value={newTimeEnd}
                  onChange={(e) => setNewTimeEnd(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="editPin">Enter Admin PIN to confirm</Label>
              <Input
                id="editPin"
                type="password"
                placeholder="Enter PIN"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleEditConfirm()}
                maxLength={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditDialog(false);
                setPinInput("");
                setEditingSlot(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleEditConfirm}>
              Update Time
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Multi-date Selection Dialog */}
      <Dialog open={showMultiDateDialog} onOpenChange={setShowMultiDateDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] flex flex-col p-0 overflow-x-hidden">
          <DialogHeader className="px-4 pt-4 sm:px-6 sm:pt-6">
            <DialogTitle className="text-base sm:text-lg">Select Multiple Dates</DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 px-4 sm:px-6 overflow-x-hidden">
            <div className="space-y-3 py-3 sm:py-4">
              <p className="text-xs sm:text-sm text-muted-foreground">
                Click on dates to select them for download. Selected dates will be highlighted.
              </p>
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={undefined}
                  onDayClick={toggleDateSelection}
                  className="pointer-events-auto mx-auto"
                  modifiers={{
                    selected: selectedDates,
                  }}
                  modifiersStyles={{
                    selected: { backgroundColor: "hsl(var(--primary))", color: "white" },
                  }}
                />
              </div>
              {selectedDates.length > 0 && (
                <div className="space-y-2 pb-3">
                  <p className="text-xs sm:text-sm font-medium">Selected: {selectedDates.length} date{selectedDates.length > 1 ? 's' : ''}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedDates
                      .sort((a, b) => a.getTime() - b.getTime())
                      .map((date, idx) => (
                        <span
                          key={idx}
                          className="text-xs bg-primary/10 px-2 py-1 rounded whitespace-nowrap"
                        >
                          {format(date, "MMM dd")}
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter className="px-4 pb-4 sm:px-6 sm:pb-6 flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowMultiDateDialog(false);
                setSelectedDates([]);
              }}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button onClick={fetchMultiDateSlots} disabled={selectedDates.length === 0} className="flex-1 sm:flex-none">
              Download {selectedDates.length > 0 && `(${selectedDates.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden multi-date downloadable view */}
      {Object.keys(multiDateSlots).length > 0 && (
          <div className="fixed top-0 left-0 w-0 h-0 overflow-hidden" aria-hidden="true">
          <div id="multi-date-download" style={{ backgroundColor: 'white', padding: '24px', width: '800px', maxWidth: '800px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px', gap: '12px' }}>
              <img src={logoImage} alt="Skylink Aviation" style={{ height: '40px', width: 'auto', flexShrink: 0 }} />
              <div style={{ textAlign: 'right', flexShrink: 0, maxWidth: '400px' }}>
                <h1 style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc2626' }}>Slot Schedule</h1>
                <p style={{ fontSize: '12px', color: '#4b5563' }}>Skylink Aviation</p>
                <p style={{ fontSize: '12px', color: '#1f2937', marginTop: '4px', fontWeight: '600' }}>
                  Multi-Date Schedule
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {Object.entries(multiDateSlots)
                .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                .map(([date, dateSlots]) => (
                  <div key={date} style={{ border: '2px solid #d1d5db', borderRadius: '8px', padding: '16px' }}>
                    <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', marginBottom: '12px', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>
                      {format(new Date(date), "PPPP")}
                    </h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {dateSlots.map((slot, idx) => (
                        <div key={slot.id} style={{ border: '1px solid #dc2626', borderRadius: '8px', padding: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <span style={{ backgroundColor: '#dc2626', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                              #{idx + 1}
                            </span>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                              {slot.time_start} - {slot.time_end}
                            </span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', fontSize: '12px' }}>
                            <div>
                              <p style={{ color: '#6b7280' }}>Instructor</p>
                              <p style={{ fontWeight: '500', color: '#111827' }}>{slot.instructor_name}</p>
                            </div>
                            <div>
                              <p style={{ color: '#6b7280' }}>Subject</p>
                              <p style={{ fontWeight: '500', color: '#111827' }}>{slot.subject_name}</p>
                            </div>
                            <div>
                              <p style={{ color: '#6b7280' }}>Batch</p>
                              <p style={{ fontWeight: '500', color: '#111827' }}>{slot.batch_name}</p>
                            </div>
                          </div>
                          {slot.students && slot.students.length > 0 && (
                            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Students:</p>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {slot.students.map((student, sidx) => (
                                  <span key={sidx} style={{ fontSize: '12px', backgroundColor: '#f3f4f6', padding: '2px 8px', borderRadius: '4px', color: '#111827' }}>
                                    {student}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>

            <div style={{ marginTop: '16px', paddingTop: '8px', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
              <p style={{ fontSize: '12px', color: '#6b7280' }}>Generated on {format(new Date(), "Pp")}</p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Are you sure you want to delete this slot? This action cannot be undone.
            </p>
            {deletingSlot && (
              <div className="bg-muted p-3 rounded-lg space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-primary" />
                  <p><strong>Instructor:</strong> {deletingSlot.instructor_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-primary" />
                  <p><strong>Subject:</strong> {deletingSlot.subject_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <p><strong>Time:</strong> {deletingSlot.time_start} - {deletingSlot.time_end}</p>
                </div>
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-primary" />
                  <p><strong>Date:</strong> {deletingSlot.slot_date && format(new Date(deletingSlot.slot_date), "PPP")}</p>
                </div>
              </div>
            )}
            {isAdmin && (
              <div className="mt-4 space-y-2">
                <Label htmlFor="deletePin" className="text-sm font-medium">Admin PIN Required</Label>
                <Input
                  id="deletePin"
                  type="password"
                  placeholder="Enter admin PIN to confirm"
                  value={pinInput}
                  onChange={(e) => setPinInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleDeleteConfirm()}
                  maxLength={6}
                  className="font-mono"
                />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setPinInput("");
                setDeletingSlot(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete Slot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
